import React from "react";
import { useNavigate } from "react-router-dom";
import { ProgressSpinner } from "primereact/progressspinner";
import {
  fetchPreferredHapTable,
  type HapTableRequest,
  type HapTableResponse,
} from "../../../hallticket-lib";
import { HapHeatmapPanel, type ResultReviewSort } from "./HapHeatmapPanel";
import { ShortlistCollegesEmpty } from "./ResultReviewEmptyState";

const ALL_LOCATIONS_VALUE = "__all_locations__";

interface ShortlistedCollegesProps {
  request: HapTableRequest;
}

export function ShortlistedColleges({ request }: ShortlistedCollegesProps) {
  const navigate = useNavigate();
  const [status, setStatus] = React.useState<"idle" | "loading" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = React.useState("");
  const [needsPreferredColleges, setNeedsPreferredColleges] = React.useState(false);
  const [data, setData] = React.useState<HapTableResponse | null>(null);
  const [sortMode, setSortMode] = React.useState<ResultReviewSort>("avg");
  const [courseFilter, setCourseFilter] = React.useState<string[]>([]);
  const [locationFilter, setLocationFilter] = React.useState<string>(ALL_LOCATIONS_VALUE);

  const runLookup = React.useCallback(async () => {
    setStatus("loading");
    setErrorMessage("");
    setNeedsPreferredColleges(false);

    try {
      const response = await fetchPreferredHapTable({
        percentile: request.percentile,
        category: request.category,
        exam: request.exam,
      });
      if (response.colleges.length === 0) {
        setNeedsPreferredColleges(true);
        setData(null);
        setStatus("success");
        return;
      }
      setData(response);
      const availableCourseNames = response.courses.map((course) => course.course_name);
      const availableCourseNameSet = new Set(availableCourseNames);
      setCourseFilter((prev) =>
        prev.length === 0
          ? availableCourseNames
          : prev.filter((courseName) => availableCourseNameSet.has(courseName)),
      );
      setLocationFilter(ALL_LOCATIONS_VALUE);
      setStatus("success");
    } catch (error) {
      const message = error instanceof Error && error.message
        ? error.message
        : "Failed to fetch shortlisted colleges.";
      setErrorMessage(message);
      setData(null);
      setStatus("error");
    }
  }, [request.category, request.exam, request.percentile]);

  React.useEffect(() => {
    void runLookup();
  }, [runLookup]);

  return (
    <div className="rr-tab-panel active">
      {errorMessage && <p className="rr-error-msg">{errorMessage}</p>}
      {needsPreferredColleges && (
        <ShortlistCollegesEmpty
          onBrowse={() => navigate("/dashboard/colleges", { state: { collegeSearchTab: "colleges" } })}
        />
      )}
      {status === "loading" && !data && (
        <div className="rr-loading-state">
          <ProgressSpinner style={{ width: "2.2rem", height: "2.2rem" }} strokeWidth="6" animationDuration=".9s" />
          <span className="rr-loading-text">Fetching preferred table...</span>
        </div>
      )}

      {data && (
        <>
          <HapHeatmapPanel
            data={data}
            status={status}
            title=""
            subtitle=""
            loadingOverlayText="Refreshing preferred table..."
            sortMode={sortMode}
            courseFilter={courseFilter}
            onSortModeChange={setSortMode}
            onCourseFilterChange={setCourseFilter}
            showLocationFilter
            locationFilter={locationFilter}
            onLocationFilterChange={setLocationFilter}
            trailingActionButton={(
              <button
                type="button"
                className="rr-shortlist-edit-btn rr-shortlist-edit-btn--toolbar"
                onClick={() => navigate("/dashboard/colleges", { state: { collegeSearchTab: "colleges" } })}
              >
                Change my list
              </button>
            )}
          />
        </>
      )}
    </div>
  );
}
